// @flow
import { Form, Input, Modal, Typography } from '@duxton-web/ui';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuid4 } from 'uuid';

import { useCreateSmartCampaign } from 'components/container/CreateSmartCampaignProvider';
import languages from 'data/languages.json';
import {
  clearPreviewMessageStore,
  SEND_PREVIEW,
  sendPreview,
} from 'omega-rtc/actions/ucd';
import { snackBar } from 'omega-rtc/components/custom/SnackBar';
import {
  HEDWIG_TEMPLATE_VARIABLES,
  SMART_CAMPAIGN_CHANNELS,
  SMART_CAMPAIGN_FORM_KEYS,
} from 'omega-rtc/constants/ucd';
import { isRequestsPending } from 'omega-rtc/utils/request-state-utils';
import {
  getRequestStateKey,
  getVariantNumberFromSmartCommsVariantName,
} from 'omega-rtc/utils/ucd-utils';
import {
  getPCIChannelPayloadForHedwigPreview,
  getPushChannelPayload,
  getSelectedVariantsDetails,
  validatePCIFieldsForPreviewMessage,
} from 'omega-rtc/utils/smart-campaign-utils';

// Util function to get snackbar message
const generateSnackBarMessage = ({
  requestDetails,
  deliveredMessages,
  isInConfirmationPage,
  language,
}) => {
  const deliveredVariants = new Set(
    ((requestDetails || {}).requestIDs || []).reduce((acc, value) => {
      const request = (deliveredMessages || []).find(
        (messageDetails) => messageDetails.requestID === value.requestID,
      );
      if (request) {
        acc.push(value.variantName);
      }
      return acc;
    }, []),
  );

  let variantStatusMessage = `${deliveredVariants.size} variants have`;

  const isRequestsSuccessful =
    requestDetails.requestIDs.length === deliveredMessages.length;

  if (
    requestDetails.requestIDs.length === 1 &&
    isRequestsSuccessful &&
    requestDetails.requestIDs[0].variantName
  ) {
    const variantNumber = getVariantNumberFromSmartCommsVariantName(
      requestDetails.requestIDs[0].variantName,
    );
    variantStatusMessage = `Variant ${variantNumber} has`;
  }

  if (
    isInConfirmationPage &&
    language &&
    languages[language] &&
    isRequestsSuccessful
  ) {
    variantStatusMessage = `Selected ${languages[language].label} content variants have`;
  }
  return `${variantStatusMessage} been sent to PAX ID: ${requestDetails.paxIDs}`;
};

const SmartCampaignSendPreview = ({
  open,
  onClose,
  language,
  selectedVariants, // Can be undefined if renderVariantFromForm is true.
  isInConfirmationPage = false,
  renderSelectedPrimaryLanguageVariants = false,
}) => {
  const dispatch = useDispatch();

  const sendPreviewRequest = useSelector((state) =>
    _.get(state.requests, getRequestStateKey(SEND_PREVIEW), {}),
  );
  const deliveredHedwigMessages = useSelector(
    (state) => state.ucd.deliveredHedwigMessages,
  );
  const undeliveredHedwigMessages = useSelector(
    (state) => state.ucd.undeliveredHedwigMessages,
  );

  const { primaryLanguageContentsInForm } = useCreateSmartCampaign();
  const [requestMetaData, setRequestMetaData] = useState({
    requestIDs: [],
    paxIDs: '',
    closeModalOnComplete: false,
    requestPending: false,
  });

  const form = Form.useFormInstance();
  const watchPaxIds = Form.useWatch(
    SMART_CAMPAIGN_FORM_KEYS.PREVIEW_MESSAGE_PAX_IDS,
    form,
  );

  // selected variants to send preview messages
  const selectedContentVariants = useMemo(() => {
    if (!open) {
      return [];
    }

    if (!renderSelectedPrimaryLanguageVariants) {
      return selectedVariants;
    }

    return getSelectedVariantsDetails({
      primaryLanguageContentsInForm,
      formInstance: form,
      language,
    });
  }, [
    renderSelectedPrimaryLanguageVariants,
    primaryLanguageContentsInForm,
    open,
  ]);

  useEffect(() => {
    if (requestMetaData.requestIDs.length > 0) {
      // On initial renter 'requestMetaData.requestIDs' will be empty array
      handleMessage();
    }
  }, [deliveredHedwigMessages, undeliveredHedwigMessages]);

  useEffect(() => {
    // on modal close
    if (!open && requestMetaData.closeModalOnComplete) {
      setRequestMetaData((prev) => ({ ...prev, closeModalOnComplete: false }));
    }
  }, [open]);

  const handleMessage = () => {
    const deliveredMessages = deliveredHedwigMessages || [];
    const undeliveredMessages = undeliveredHedwigMessages || [];

    const isRequestCompleted =
      deliveredMessages.length + undeliveredMessages.length ===
      requestMetaData.requestIDs.length;

    // isRequestCompleted is false then, request is still pending
    if (!isRequestCompleted) {
      return;
    }

    // success snackbar
    if (deliveredMessages.length) {
      const messageContent = generateSnackBarMessage({
        requestDetails: requestMetaData,
        deliveredMessages,
        isInConfirmationPage,
        language,
      });
      if (!messageContent) {
        return;
      }
      snackBar.success({ content: messageContent });
    }

    // If all request is failed we will show a waring snackbar
    if (undeliveredMessages.length === requestMetaData.requestIDs.length) {
      snackBar.warning({
        content: 'The preview message could not be sent. Please try again.',
      });
    }

    // close modal if it is enabled.
    if (requestMetaData.closeModalOnComplete) {
      onClose();
    }

    // Clearing requestMetaData on success of request...
    setRequestMetaData({
      paxIDs: '',
      requestIDs: [],
      closeModalOnComplete: false,
      requestPending: false,
    });
    dispatch(clearPreviewMessageStore());
    setRequestMetaData((prev) => ({ ...prev, requestPending: false }));
  };

  const validatePCIRequiredFields = () => {
    const errorFields = validatePCIFieldsForPreviewMessage({
      formInstance: form,
      language,
    });

    if (errorFields.length) {
      snackBar.warning({ content: `Please input ${errorFields[0]}` });
      return true;
    }

    return false;
  };

  const sendPreviewMessageForVariant = ({ variant }) => {
    const channel = form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.CHANNEL);
    const listOfPaxID = watchPaxIds.split(/[\s,]+/).filter(Boolean);
    const metaDataForPushChannel = {
      actionType: form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.ACTION_TYPE),
      actionURL: form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.ACTION_URL),
      imageUrl: form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.PN_IMAGE_URL) || '',
    };

    const { actionType, actionURL } = metaDataForPushChannel;
    const actionTypeVariableMap = {
      deeplink: HEDWIG_TEMPLATE_VARIABLES.DEEPLINKURL,
      webUrl: HEDWIG_TEMPLATE_VARIABLES.WEBURL,
    };

    Object.keys(actionTypeVariableMap).forEach((key) => {
      metaDataForPushChannel[actionTypeVariableMap[key]] = key === actionType ? actionURL : '';
    });

    let templateParams = {};
    const titleAndMessage = {
      title: variant.title, // NOTE: personal attribute UI labels will be passed to preview instead of value(eg: value:  {{ persona.passenger_name }}, label: { Passenger name } )
      message: variant.message,
    };

    switch (channel) {
      case SMART_CAMPAIGN_CHANNELS.push:
        templateParams = getPushChannelPayload({
          ...titleAndMessage,
          metaDataForPushChannel,
        });
        break;
      case SMART_CAMPAIGN_CHANNELS.pci: {
        templateParams = getPCIChannelPayloadForHedwigPreview({
          language,
          ...titleAndMessage,
          formInstance: form,
        });
        break;
      }
      default:
        break;
    }

    listOfPaxID.forEach((paxId) => {
      const requestID = uuid4();
      const payloadForSendPreview = {
        language,
        channel,
        templateParams,
        requestID,
        recipientID: paxId,
      };

      dispatch(sendPreview(payloadForSendPreview));

      setRequestMetaData((prev) => ({
        ...prev,
        requestIDs: [
          ...prev.requestIDs,
          { variantName: variant.variantName, requestID },
        ],
      }));
    });
  };

  const sendPreviewMessages = async () => {
    if (!selectedContentVariants || !selectedContentVariants.length) {
      snackBar.warning({
        content:
          'Please select content variants before sending preview message',
      });
      return;
    }

    const channel = form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.CHANNEL);
    if (
      channel === SMART_CAMPAIGN_CHANNELS.pci &&
      validatePCIRequiredFields()
    ) {
      return;
    }

    setRequestMetaData((prev) => ({
      ...prev,
      paxIDs: watchPaxIds,
      closeModalOnComplete: true,
      requestPending: true,
    }));
    selectedContentVariants.forEach((variant) => {
      sendPreviewMessageForVariant({ variant });
    });
  };

  const onOk = () => {
    form
      .validateFields([SMART_CAMPAIGN_FORM_KEYS.PREVIEW_MESSAGE_PAX_IDS])
      .then(sendPreviewMessages)
      .catch((error) => {
        console.log(error);
      });
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      centered
      title="Send preview message"
      onOk={onOk}
      confirmLoading={isRequestsPending(sendPreviewRequest)}
      cancelButtonProps={{
        type: 'text',
        disabled: requestMetaData.requestPending,
      }}
      okText="Send"
      okButtonProps={{
        disabled: !watchPaxIds,
        loading: requestMetaData.requestPending,
      }}
      width={440}
    >
      <Typography.Body level={3}>
        Input the PAX ID of the Grab app on your mobile device to receive the
        message preview. Separate multiple PAX IDs with a comma.
      </Typography.Body>
      <Form.Item
        name={SMART_CAMPAIGN_FORM_KEYS.PREVIEW_MESSAGE_PAX_IDS}
        label="PAX IDs"
        required
        rules={[
          {
            required: true,
            message: 'Please enter PAX IDs separated by comma (,)',
          },
          {
            validator: (__, value) => {
              if (!value) {
                return Promise.resolve(); // To prevent double invalidation
              }
              const paxIds = value && value.split(/[\s,]+/).filter(Boolean);
              if (paxIds && paxIds.length > 5) {
                return Promise.reject(
                  new Error('You can only enter up to 5 PAX IDs'),
                );
              }
              if (paxIds.some((paxId) => !/^\d+$/.test(paxId))) {
                return Promise.reject(new Error('PAX IDs must be numbers'));
              }

              return Promise.resolve();
            },
          },
        ]}
      >
        <Input style={{ width: 392 }} placeholder="Enter PAX IDs" />
      </Form.Item>
    </Modal>
  );
};

export default SmartCampaignSendPreview;
